import { activateScrollScenes } from './scroll-scenes';

const LANDING_TOLERANCE_PX = 12;
const MAX_CORRECTIONS = 3;
const SETTLE_MIN_MS = 320;
const SETTLE_TIMEOUT_MS = 1200;
const SETTLE_STILL_FRAMES = 6;

const HEIGHT_STILL_FRAMES = 3;
const HEIGHT_TIMEOUT_MS = 700;

/** Distance covered by the closing glide. The rest is closed instantly. */
const GLIDE_PX = 520;
const MAX_APPROACH_HOPS = 6;

function nextFrame(): Promise<void> {
  return new Promise((resolve) => requestAnimationFrame(() => resolve()));
}

/**
 * Resolves once scrolling has stopped. A smooth scroll takes a few frames to
 * start, so ignore stillness until SETTLE_MIN_MS has passed - otherwise this
 * reports "arrived" before the browser has moved at all.
 */
function settleScroll(): Promise<void> {
  return new Promise((resolve) => {
    const started = performance.now();
    let last = window.scrollY;
    let still = 0;

    const check = () => {
      const elapsed = performance.now() - started;

      if (window.scrollY === last) {
        still += 1;
      } else {
        still = 0;
        last = window.scrollY;
      }

      if ((elapsed >= SETTLE_MIN_MS && still >= SETTLE_STILL_FRAMES) || elapsed >= SETTLE_TIMEOUT_MS) {
        resolve();
        return;
      }

      requestAnimationFrame(check);
    };

    requestAnimationFrame(check);
  });
}

/**
 * Resolves once the document has stopped growing. Building the pinned scenes
 * loads GSAP and then lays out three pin spacers, which between them add more
 * than 5,000px. Aiming before that settles lands a screen or more short and
 * then re-aims in full view of the visitor.
 */
async function settleHeight(): Promise<void> {
  const started = performance.now();
  let last = document.documentElement.scrollHeight;
  let still = 0;

  while (performance.now() - started < HEIGHT_TIMEOUT_MS) {
    await nextFrame();
    const height = document.documentElement.scrollHeight;

    if (height === last) {
      still += 1;

      if (still >= HEIGHT_STILL_FRAMES) {
        return;
      }
    } else {
      still = 0;
      last = height;
    }
  }
}

function scrollMarginTop(element: HTMLElement): number {
  const margin = Number.parseFloat(getComputedStyle(element).scrollMarginTop);
  return Number.isFinite(margin) ? margin : 0;
}

function landingOffset(sectionId: string): number | null {
  const target = document.getElementById(sectionId);
  return target ? target.getBoundingClientRect().top - scrollMarginTop(target) : null;
}

/**
 * Scrolls to a section and lands on it.
 *
 * Smooth-scrolling the whole way does not work here: the page is over 13,000px
 * tall, a native smooth scroll across it takes seconds, and the pinned scenes
 * finish building while it is still in flight, so it arrives somewhere else.
 * Close the distance instantly instead - each hop forces the stretch it lands
 * in to lay out - and keep a short glide at the end so the arrival still reads
 * as a scroll rather than a page swap.
 */
export async function scrollToSection(sectionId: string): Promise<void> {
  if (!document.getElementById(sectionId)) {
    return;
  }

  activateScrollScenes();

  for (let hop = 0; hop < MAX_APPROACH_HOPS; hop += 1) {
    const offset = landingOffset(sectionId);

    if (offset === null || Math.abs(offset) <= GLIDE_PX) {
      break;
    }

    window.scrollTo({ top: window.scrollY + offset - Math.sign(offset) * GLIDE_PX, behavior: 'auto' });
    await nextFrame();
    await nextFrame();
  }

  // A pin spacer appears a beat after its section is approached. Without this
  // the closing glide aims at a number that is already stale.
  await settleHeight();

  for (let attempt = 0; attempt < MAX_CORRECTIONS; attempt += 1) {
    const offset = landingOffset(sectionId);

    if (offset === null || Math.abs(offset) <= LANDING_TOLERANCE_PX) {
      return;
    }

    window.scrollBy({ top: offset, behavior: 'smooth' });
    await settleScroll();
  }
}

export function scrollToTop(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}
